import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import TailwindButton from '../components/TailwindButton';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import type { StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { TAKWEEN_SURVEY_FORM_ID } from './constants';
import { TakweenSurveyRouteState } from './types';
import { useDirectSurveyCompletions } from './useDirectSurveyCompletions';

const TakweenStudentSurveyGrid: React.FC = () => {
  const classroom = useSelector((state: RootState) => selectClassroom(state));
  const history = useHistory();
  const location = useLocation<TakweenSurveyRouteState>();
  const students = classroom?.listOfStudentProxies || [];
  const { completedStudentUserIdSet, isLoading, error, refresh } = useDirectSurveyCompletions({
    classroomId: classroom?.classroomId,
    formId: TAKWEEN_SURVEY_FORM_ID,
  });

  // Optimistic override: treat a just-submitted student as completed immediately,
  // before backend processing has propagated to the completion query.
  const optimisticCompletedId = location.state?.justCompletedStudentId;

  const getInitials = (student: StudentProxyType): string => {
    const first = student.firstName?.[0] || '';
    const last = student.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase();
  };

  const buildBackPath = (): string => {
    if (location.state?.unitId && location.state?.language && location.state?.grade) {
      return `/lessonPlan/${location.state.unitId}/${location.state.language}/${location.state.grade}`;
    }

    return '/library';
  };

  const handleStudentClick = (studentId: string) => {
    if (!classroom?.classroomId) {
      return;
    }

    history.push(`/takween/survey/${classroom.classroomId}/${studentId}`, {
      ...location.state,
    });
  };

  return (
    <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
      <div className='mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between'>
        <div>
          <h1 className='m-0 text-[3rem] font-bold text-gray-800'>Student Post-Survey</h1>
          <p className='mb-0 mt-2 text-[2rem] text-gray-500'>Click on a student to begin the post-survey</p>
          {isLoading && <p className='mb-0 mt-2 text-[1.4rem] text-gray-500'>Checking completion status...</p>}
          {error && (
            <p className='mb-0 mt-2 text-[1.4rem] text-amber-700'>
              Completion status is unavailable right now.
              <button
                type='button'
                className='ml-2 cursor-pointer border-none bg-transparent p-0 text-[1.4rem] font-semibold text-orange-600 underline'
                onClick={() => {
                  void refresh();
                }}
              >
                Retry
              </button>
            </p>
          )}
        </div>
        <div className='w-[196px]'>
          <TailwindButton variant='secondary' onClick={() => history.push(buildBackPath())} className='w-full'>
            Back to Unit
          </TailwindButton>
        </div>
      </div>

      {students.length === 0 ? (
        <div className='rounded-lg bg-white p-8 text-center shadow-sm'>
          <p className='m-0 text-[1.6rem] text-gray-500'>No students found in this classroom.</p>
        </div>
      ) : (
        <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
          {students.map((student) => {
            const fullName = `${student.firstName} ${student.lastName || ''}`.trim();
            const isCompleted =
              student.userId === optimisticCompletedId || (!isLoading && completedStudentUserIdSet.has(student.userId));

            return (
              <button
                key={student.userId}
                onClick={() => handleStudentClick(student.userId)}
                className={[
                  'relative flex min-h-[19rem] flex-col items-center justify-center rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300',
                  'cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:cursor-not-allowed',
                  'enabled:hover:border-orange-200 enabled:hover:shadow-lg',
                  isCompleted ? 'opacity-60' : '',
                ].join(' ')}
                disabled={isCompleted}
              >
                <div className='mb-4 flex justify-center'>
                  <div
                    className={[
                      'flex h-20 w-20 items-center justify-center rounded-full text-[2rem] font-bold text-white md:h-[5.6rem] md:w-[5.6rem]',
                      isCompleted ? 'bg-emerald-500' : 'bg-gray-400',
                    ].join(' ')}
                  >
                    {getInitials(student)}
                  </div>
                </div>

                <h3 className='mb-2 overflow-hidden text-ellipsis whitespace-nowrap text-center text-[1.9rem] font-semibold text-gray-800'>
                  {fullName}
                </h3>

                <div className='flex justify-center'>
                  <span
                    className={[
                      'inline-flex items-center rounded-full px-3 py-1 text-[1.4rem] font-medium',
                      isLoading
                        ? 'bg-gray-100 text-gray-500'
                        : isCompleted
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-gray-100 text-gray-500',
                    ].join(' ')}
                  >
                    {isLoading ? 'Checking...' : isCompleted ? 'Survey Completed' : 'Not yet'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TakweenStudentSurveyGrid;
